import React, { useEffect, useRef } from "react";
import { Banknote, Briefcase, CalendarDays, CalendarPlus, FileSignature, Flag, History, Home, Lock, Menu, MessageSquare, NotebookPen, PiggyBank, PlayCircle, Plus, Receipt, Target, UserRound, Wallet } from "lucide-react";
import { TeaLogo } from "@/components/TeaLogo";

const ORDER = ["pillars", "question", "now", "goal", "why", "progress", "private", "unlock"];
const TABS = [["home", "Home", Home], ["money", "Money", Wallet], ["calendar", "Calendar", CalendarDays], ["program", "Program", Target], ["more", "More", Menu]];
const card = "rounded-lg border border-white/10 bg-white/[0.04] p-3";

function Spot({ k, focus, className = "", children }) {
  const on = focus === k;
  return (
    <div data-tour={k} data-testid={`welcome-spot-${k}`} className={`rounded-lg transition-[opacity,box-shadow] duration-500 ${on ? "relative z-10 ring-2 ring-[#d4a017] shadow-[0_0_0_6px_rgba(212,160,23,0.18),0_0_36px_rgba(212,160,23,0.35)]" : focus ? "opacity-30" : ""} ${className}`}>{children}</div>
  );
}

function MiniScale({ label, pick, min = 1 }) {
  return (
    <div className="space-y-1.5">
      <div className="text-xs text-white/70">{label}</div>
      <div className="grid grid-cols-10 gap-1">{Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
        <span key={n} className={`h-7 rounded text-[11px] font-semibold grid place-items-center transition-colors duration-500 ${n === pick ? "bg-[#d4a017] text-black" : n < min ? "bg-white/[0.03] text-white/15" : "bg-white/[0.07] text-white/70"}`}>{n}</span>))}</div>
    </div>
  );
}

function MockBaseline({ focus }) {
  const at = (k) => ORDER.indexOf(focus) >= ORDER.indexOf(k);
  return (
    <div className="space-y-4 p-4">
      <Spot k="progress" focus={focus} className="p-1"><div className="h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-[#d4a017] transition-[width] duration-700" style={{ width: at("progress") ? "40%" : "7%" }} /></div></Spot>
      <Spot k="pillars" focus={focus} className="grid grid-cols-3 gap-1.5 p-1">
        {["Health", "Wealth", "Spiritual Growth"].map((p) => <div key={p} className={`${card} !p-2 text-[11px] leading-tight`}>{p}<div className="text-[10px] text-white/45 mt-0.5">10 questions</div></div>)}
      </Spot>
      <Spot k="question" focus={focus} className="p-2 space-y-1">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#d4a017]">Health — Mind & Body · 3 of 30</div>
        <div className="text-lg font-semibold">Sleep & Recovery Protocols</div>
        <p className="text-xs text-white/65">How steady is your sleep — a regular bedtime and enough rest to work and think clearly?</p>
      </Spot>
      <Spot k="now" focus={focus} className="p-2"><MiniScale label="Where are you right now?" pick={at("now") ? 4 : 0} /></Spot>
      <Spot k="goal" focus={focus} className="p-2"><MiniScale label="Where do you want to be?" pick={at("goal") ? 8 : 0} min={at("now") ? 4 : 1} /></Spot>
      <Spot k="why" focus={focus} className="p-2 space-y-1">
        <div className="text-xs text-white/70">Why? Tell us in your own words.</div>
        <div className="min-h-[52px] rounded-md bg-black/40 border border-white/15 px-2.5 py-2 text-xs text-white/85">{at("why") ? "I stay up too late on my phone. I want a set bedtime so I'm sharp for work." : <span className="text-white/30">What does this look like for you today?</span>}</div>
      </Spot>
      <Spot k="private" focus={focus} className="p-2 flex items-center gap-2 text-xs text-white/70"><Lock className="h-4 w-4 text-[#d4a017]" /> Private — only you and your TEA support team can see this</Spot>
      <Spot k="unlock" focus={focus} className="p-1"><div className="rounded-md bg-[#d4a017] text-black text-sm font-semibold text-center py-2.5">Set my starting line</div></Spot>
    </div>
  );
}

const MTile = ({ icon: Icon, label, sub, accent }) => (
  <div className={`${card} ${accent ? "border-[#d4a017]/50 bg-[#d4a017]/10" : ""}`}><Icon className="h-4 w-4 text-[#d4a017]" /><div className="mt-1.5 text-sm font-medium">{label}</div><div className="text-[11px] text-white/50">{sub}</div></div>
);

const SCREENS = {
  home: (name) => (<>
    <div className="text-2xl font-semibold">Hi {name}.</div>
    <p className="text-[11px] text-white/55">Phase 1 · Day 12 · 18% of the program complete</p>
    <div className={`${card} flex justify-between text-sm`}><span>PACT Life Pass</span><span className="text-[#d4a017]">Active</span></div>
    <div className={`${card} flex justify-between text-sm`}><span>Room ready</span><span className="text-emerald-300">Passed</span></div>
    <div className="grid grid-cols-2 gap-2"><MTile icon={CalendarPlus} label="Book a session" sub="Open classes & sessions" accent /><MTile icon={MessageSquare} label="Memos" sub="Messages from staff" /></div>
  </>),
  money: () => (<div className="grid grid-cols-2 gap-2">
    <MTile icon={Banknote} label="Log paycheck" sub="Put every dollar where it goes" accent /><MTile icon={PiggyBank} label="Savings" sub="$240.00 available" />
    <MTile icon={Receipt} label="Expenses" sub="$85.00 this month" /><MTile icon={History} label="My paychecks" sub="3 logged" />
  </div>),
  calendar: () => (<>
    <div className={`${card} space-y-2`}><div className="flex justify-between text-sm"><span className="text-[#d4a017] text-[11px] uppercase tracking-[0.2em]">My work week</span><span className="text-xs text-white/60">32 hrs</span></div>
      <div className="grid grid-cols-7 gap-1 text-center text-[10px]">{["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d, i) => <div key={d} className={`rounded p-1 ${i < 4 ? "bg-[#d4a017]/15 text-[#f0d27a]" : "bg-white/[0.03] text-white/40"}`}>{d}<div className="font-semibold">{i < 4 ? 8 : "—"}</div></div>)}</div></div>
    <div className="grid grid-cols-2 gap-2 text-xs"><div className="rounded-md bg-[#d4a017] text-black font-semibold py-2 flex items-center justify-center gap-1"><Briefcase className="h-3.5 w-3.5" /> Set my work week</div><div className="rounded-md bg-[#d4a017] text-black font-semibold py-2 flex items-center justify-center gap-1"><Plus className="h-3.5 w-3.5" /> Add an event</div></div>
    <div className={card}><div className="text-sm font-medium">Court check-in</div><div className="text-[11px] text-white/50">Thu 9:00 AM · County Courthouse</div></div>
  </>),
  program: () => (<>
    <MTile icon={Flag} label="My Roadmap" sub="Your pillars, progress and milestones" accent />
    <div className="grid grid-cols-2 gap-2"><MTile icon={Target} label="Pillar tasks" sub="1 open" /><MTile icon={NotebookPen} label="Homework" sub="1 lesson to do" /></div>
  </>),
  more: () => (<>
    <div className={`${card} flex items-center gap-2 text-sm`}><UserRound className="h-4 w-4 text-[#d4a017]" /> My profile</div>
    <div className={`${card} space-y-1.5`}><div className="text-[11px] uppercase tracking-[0.2em] text-[#d4a017]">Your documents</div>
      {["House rules agreement", "Program participation"].map((t) => <div key={t} className="flex items-center gap-2 text-xs text-white/75"><FileSignature className="h-3.5 w-3.5" /> {t}</div>)}</div>
    <div className={`${card} flex items-center gap-2 text-sm`}><PlayCircle className="h-4 w-4 text-[#d4a017]" /> Replay welcome tour</div>
  </>),
};

function MockApp({ tab, name }) {
  return (
    <div className="flex flex-col h-full">
      <Spot k={`${tab}-screen`} focus={`${tab}-screen`} className="flex-1 mx-3 mb-3 mt-7 p-3 space-y-3">{SCREENS[tab](name)}</Spot>
      <div className="grid grid-cols-5 border-t border-white/10 bg-black/30">
        {TABS.map(([k, l, Icon]) => <div key={k} className={`flex flex-col items-center gap-0.5 py-2 text-[10px] transition-colors duration-500 ${k === tab ? "text-[#d4a017] bg-[#d4a017]/10" : "text-white/40"}`}><Icon className="h-4 w-4" />{l}</div>)}
      </div>
    </div>
  );
}

function Cover({ focus, name }) {
  return (
    <div className="h-full grid place-items-center p-6 text-center">
      <div className="space-y-4">
        <div className="mx-auto w-fit animate-pulse"><TeaLogo size={72} testid="welcome-cover-logo" /></div>
        {focus === "leaders" ? <blockquote className="text-xl font-semibold leading-snug">"We believe in you, {name}. We'll be right here beside you."<div className="mt-2 text-xs uppercase tracking-[0.25em] text-[#d4a017]">— TEA Leadership</div></blockquote>
          : <div className="text-2xl font-semibold">Welcome, {name}.</div>}
      </div>
    </div>
  );
}

/** Sample screens the welcome tour narrates over; the spoken part is highlighted. */
export function MockPortal({ step, name }) {
  const box = useRef(null);
  useEffect(() => { box.current?.querySelector(`[data-tour="${step.focus}"]`)?.scrollIntoView({ block: "nearest", behavior: "smooth" }); }, [step]);
  return (
    <div ref={box} data-testid={`welcome-mock-${step.scene}`} className="relative h-full overflow-y-auto rounded-2xl border border-white/10 bg-[#0b0814] shadow-2xl">
      <span className="absolute right-3 top-2 z-20 text-[9px] uppercase tracking-[0.25em] text-white/35">Sample screen</span>
      {step.scene === "cover" ? <Cover focus={step.focus} name={name} /> : step.scene === "baseline" ? <MockBaseline focus={step.focus} /> : <MockApp tab={step.focus} name={name} />}
    </div>
  );
}
